/**
 * ADR-173 D2 — the fixed-term block, one implementation: the contract end
 * date and its comment, with the ADR-136 A2 auto-generated sentence.
 *
 * Interaction model (the hire one, now everywhere): an EMPTY end date means
 * an open-ended contract; setting it makes the contract fixed-term and
 * reveals the comment. `endRequired` turns the end date mandatory (Add
 * Contract when the primary has a scheduled end — ADR-153 R1; Add End Date).
 *
 * The comment is generated from start / end / contract number and keeps
 * regenerating until the operator edits it; clearing the end date re-arms
 * generation. Dates in and out are 'YYYY-MM-DD' strings, shown as UTC days.
 */
import React, { useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { buildFixedTermComment } from '../../../constants/fixedTermContracts';
import { CONTRACT_TERM_LABELS, CONTRACT_TERM_HELPER_TEXTS, fixedTermEndProblem } from '../../../constants/contractTerms';

dayjs.extend(utc);

const toYmd = (value) => (
  value && typeof value.isValid === 'function' && value.isValid() ? value.format('YYYY-MM-DD') : null
);

const FixedTermFields = ({
  startYmd,
  endYmd,
  onEndChange,
  comment,
  onCommentChange,
  contractNumber = '',
  endRequired = false,
  minEndYmd = null,
  maxEndYmd = null,
  startLabel = 'contract start date',
  endHelperText = null,
  endProblem = null,
  commentRequiredText = CONTRACT_TERM_HELPER_TEXTS.fixedTermCommentRequired,
  commentHelperText = CONTRACT_TERM_HELPER_TEXTS.fixedTermComment,
  disabled = false
}) => {
  const [commentTouched, setCommentTouched] = useState(false);

  // ADR-136 A2 — generate at the entry surface; regenerate until edited.
  useEffect(() => {
    if (commentTouched) {
      return;
    }
    const generated = endYmd ? (buildFixedTermComment(startYmd, endYmd, contractNumber) || '') : '';
    if ((comment || '') !== generated) {
      onCommentChange(generated);
    }
  }, [commentTouched, startYmd, endYmd, contractNumber, comment, onCommentChange]);

  const problem = endProblem || fixedTermEndProblem({ startYmd, endYmd, minEndYmd, maxEndYmd, startLabel });
  const missing = endRequired && !endYmd;
  const pickerMin = [minEndYmd, startYmd ? dayjs.utc(startYmd).add(1, 'day').format('YYYY-MM-DD') : null]
    .filter(Boolean).sort().pop() || null;
  const commentBlank = !String(comment || '').trim();

  return (
    <>
      <DatePicker
        label={CONTRACT_TERM_LABELS.contractEndDate}
        value={endYmd ? dayjs.utc(endYmd) : null}
        onChange={(newValue) => {
          const ymd = toYmd(newValue);
          if (ymd) {
            onEndChange(ymd);
          } else if (newValue === null) {
            // Clearing the end date makes the contract open-ended and re-arms generation
            setCommentTouched(false);
            onEndChange(null);
          }
        }}
        format="DD/MM/YYYY"
        minDate={pickerMin ? dayjs.utc(pickerMin) : undefined}
        maxDate={maxEndYmd ? dayjs.utc(maxEndYmd) : undefined}
        disabled={disabled}
        slotProps={{
          field: { clearable: !endRequired },
          textField: {
            fullWidth: true,
            required: endRequired,
            error: Boolean(problem),
            helperText: problem
              || endHelperText
              || (missing ? null : CONTRACT_TERM_HELPER_TEXTS.contractEndDateOptional),
            inputProps: { placeholder: 'DD/MM/YYYY' }
          }
        }}
      />
      {endYmd && (
        <TextField
          label={CONTRACT_TERM_LABELS.fixedTermComment}
          fullWidth
          multiline
          minRows={2}
          required
          value={comment || ''}
          onChange={(event) => {
            setCommentTouched(true);
            onCommentChange(event.target.value);
          }}
          disabled={disabled}
          error={commentBlank}
          helperText={commentBlank ? commentRequiredText : commentHelperText}
        />
      )}
    </>
  );
};

export default FixedTermFields;
